import { type FeedbackCategory, MAX_SCREENSHOTS } from "./feedbackForm";

// Send waits this long for a capture still running, then sends without it.
export const SEND_WAIT_MS = 3000;

export interface PageCaptureState {
  /** The captured page: undefined while capturing, null if it failed. */
  result: File | null | undefined;
  /** I removed it. Final for this dialog: switching category doesn't bring it back. */
  removed: boolean;
}

/** How the page screenshot shows in the Screenshots section: "none" means it isn't sent. */
export type PageCaptureSlot = "capturing" | "attached" | "failed" | "none";

/**
 * The page screenshot's place (docs/spec/shell/feedback.md SH-FB-08): only on bug reports, until I
 * remove it. While capturing or attached it takes one of the MAX_SCREENSHOTS places; if my own
 * screenshots fill them all (added while it was a feature request), it takes none, even while still
 * capturing, until I remove one of mine.
 */
export function pageCaptureSlot(
  state: PageCaptureState,
  category: FeedbackCategory,
  fileCount: number,
): PageCaptureSlot {
  if (state.removed || category !== "bug") return "none";
  if (state.result === null) return "failed";
  if (fileCount >= MAX_SCREENSHOTS) return "none";
  return state.result === undefined ? "capturing" : "attached";
}

/** Places the page screenshot takes from MAX_SCREENSHOTS. */
export const reservedPlaces = (slot: PageCaptureSlot) => (slot === "capturing" || slot === "attached" ? 1 : 0);

/** `promise`'s value, or undefined if it hasn't settled within `ms` (a rejection still rejects). */
export function settleWithin<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
